import { createContext, useContext } from 'react';
import type { Field, MediaConfig, Problem } from '@hotpot/core';

export interface EditorCtx {
  /** Repo path of the file being edited (a stand-in path in the collection folder for new entries). */
  entryPath: string;
  defaultLocale: string;
  /** Locale being edited; equals defaultLocale unless translating. */
  locale: string;
  /** `field_suffix` translations are edited next to the main text; `multiple_files` swaps the whole file. */
  i18nMode: 'none' | 'field_suffix' | 'multiple_files';
  /** Data in the default locale for reference while translating (multiple_files). */
  reference?: Record<string, unknown>;
  mediaFor: (field?: { media?: MediaConfig }) => MediaConfig;
  /** Prepare and queue images for upload with the next save; returns the values to store. */
  upload: (files: File[], field: { media?: MediaConfig }) => Promise<string[]>;
  /** URL to show an image value (pending uploads included). */
  imageSrc: (value: string, field?: { media?: MediaConfig }) => string | undefined;
  /** Open the media picker for a field. */
  pick: (field: { media?: MediaConfig }, multiple: boolean) => Promise<string[] | null>;
  problems: Problem[];
  showAdvanced: boolean;
  /** `i18n.layout: side_by_side` (field_suffix): translatable fields show the main text and the translation together, both editable. */
  sideBySide: boolean;
  /** `i18n.auto_translate`: typing the main text fills in a machine translation (with sideBySide). */
  autoTranslate: boolean;
}

export const EditorContext = createContext<EditorCtx>(null!);
export const useEditor = () => useContext(EditorContext);

/** Does this field (or anything inside it) have a translation in the given locale? */
export function hasTranslatable(field: Field, locale: string, mode: EditorCtx['i18nMode']): boolean {
  if (mode === 'multiple_files') return field.i18n !== false;
  if (field.translations?.[locale]) return true;
  if (field.type === 'object') return field.fields.some((f) => hasTranslatable(f, locale, mode));
  if (field.type === 'list' && field.fields) return field.fields.some((f) => hasTranslatable(f, locale, mode));
  if (field.type === 'list' && field.types) return field.types.some((t) => t.fields.some((f) => hasTranslatable(f, locale, mode)));
  return false;
}
